import { UIView } from "uicore-ts"
import { RHBadges } from "./Utils_slop/RHDesignTokens"


/**
 * RHFilterChipBarView — lays a flat list of filter chips out as a left-packed,
 * wrapping row group. Each chip keeps its intrinsic (label-hugging) width; when
 * the next chip would overflow the available width it starts a new row.
 *
 * This is a dedicated UIView subclass precisely because its height depends on how
 * many rows the chips wrap into. The framework derives that height automatically
 * from the chip frames during virtual layouting (bounds.height is 0 there, but
 * bounds.width is real — and the wrap only ever reads the width), so there is no
 * need to override intrinsicContentHeight.
 */
export class RHFilterChipBarView extends UIView {


    _chips: UIView[] = []
    rowHeight = RHBadges.filterChip.rowHeight


    get chips(): UIView[] {
        return this._chips
    }

    set chips(value: UIView[]) {
        for (const existingChip of this._chips) {
            existingChip.removeFromSuperview()
        }
        this._chips = value
        for (const chip of value) {
            chip.addedAsSubviewToView(this)
        }
        this.setNeedsLayout()
    }


    // ── Layout ──────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        const rowHeight = this.rowHeight
        const columnGap = RHBadges.filterChip.columnGap
        const rowGap = RHBadges.filterChip.rowGap

        const chipRows = this._chipRowsFittingWidth(bounds.width, rowHeight, columnGap)

        // A loop over a dynamic number of rows (the documented exception to the
        // single declarative chain): each row is its own left-packed,
        // intrinsic-width row, stacked under the previous one.
        let rowFrame = bounds.rectangleWithHeight(rowHeight)
        chipRows.forEach((chipRow, rowIndex) => {
            if (rowIndex > 0) {
                rowFrame = rowFrame.rectangleForNextRow(rowGap, rowHeight)
            }
            const chipWidths = chipRow.map(chip => this._chipWidth(chip, rowHeight))
            rowFrame.framesByDistributingViewsAsRow(chipRow, columnGap, chipWidths)
        })
    }


    // Greedy wrap: accumulate chips into the current row until the next chip would
    // exceed the available width, then start a new row. A row always keeps at
    // least one chip, even when a single chip is wider than the bar.
    _chipRowsFittingWidth(availableWidth: number, rowHeight: number, columnGap: number): UIView[][] {
        if (this._chips.length === 0) {
            return []
        }
        // Guard the virtual/zero-width measurement pass: a single row keeps the
        // measured height sane until a real width is available to wrap against.
        if (availableWidth <= 0) {
            return [this._chips.slice()]
        }

        const chipRows: UIView[][] = []
        let currentRow: UIView[] = []
        let currentRowWidth = 0

        for (const chip of this._chips) {
            const chipWidth = this._chipWidth(chip, rowHeight)
            let widthIfAppended = chipWidth
            if (currentRow.length > 0) {
                widthIfAppended = currentRowWidth + columnGap + chipWidth
            }

            if (currentRow.length > 0 && widthIfAppended > availableWidth) {
                chipRows.push(currentRow)
                currentRow = []
                currentRowWidth = 0
            }

            if (currentRow.length === 0) {
                currentRowWidth = chipWidth
            }
            else {
                currentRowWidth = currentRowWidth + columnGap + chipWidth
            }
            currentRow.push(chip)
        }

        if (currentRow.length > 0) {
            chipRows.push(currentRow)
        }
        return chipRows
    }


    _chipWidth(chip: UIView, rowHeight: number): number {
        return chip.intrinsicContentWidth(rowHeight)
    }

}
